/**
 * Preparing an MCA return — fill it, check it, and keep it, here.
 *
 * What this screen is: staging. MCA21 V3 times out and loses sessions, so the
 * return is prepared here, over as many sittings as it takes, against data
 * ComplyV already holds. What it is not: a filing. MCA has no submission API
 * and the form is signed with a DSC on the director's own machine — so the
 * page says so at the top and the only "filed" control RECORDS the SRN after
 * the person has done it on the portal.
 *
 * Two things on screen exist to protect the person signing:
 *   - every answer says whether ComplyV filled it or they did;
 *   - what is missing and what looks wrong are shown apart, because "you left
 *     this blank" and "this figure looks off" need different responses.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiRefreshCw, FiDownload, FiCheckCircle, FiAlertTriangle, FiPlus, FiX, FiInfo } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import { mcaFormsAPI } from '../services/api';

const TEAL = '#145f5f';
const GOLD = '#8a6a1f';
const MUTED = '#7a7060';
const BORDER = '#ddd6c6';

const errText = (e) => e?.response?.data?.message || e?.message || 'Something went wrong.';

/** The financial year containing today: April to March. "2025-26". */
export function currentFinancialYear(now = new Date()) {
  const start = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** This year and the last few — returns are routinely filed for the year before. */
export function financialYearOptions(now = new Date(), count = 4) {
  const [startStr] = currentFinancialYear(now).split('-');
  const start = Number(startStr);
  return Array.from({ length: count }, (_, i) => {
    const y = start - i;
    return `${y}-${String((y + 1) % 100).padStart(2, '0')}`;
  });
}

const isBlank = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

/** One answer's control. The type comes from the form definition. */
function FieldInput({ field, value, onChange, disabled }) {
  const base = 'w-full px-3 py-2 rounded-lg text-[13px] outline-none disabled:opacity-60';
  const style = { background: '#fffdf8', border: `1px solid ${BORDER}`, color: '#08111f' };

  if (field.type === 'boolean') {
    return (
      <select
        className={base}
        style={style}
        disabled={disabled}
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : e.target.value === 'yes')}
      >
        <option value="">Not answered</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  }

  if (field.type === 'table') return <TableInput field={field} value={value} onChange={onChange} disabled={disabled} />;

  if (field.type === 'text') {
    return (
      <textarea
        className={base}
        style={style}
        rows={2}
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const inputType = { date: 'date', email: 'email', number: 'number', currency: 'number' }[field.type] || 'text';
  // A date arrives as an ISO timestamp from the company record; an <input
  // type=date> only understands the first ten characters of it.
  const shown = field.type === 'date' && typeof value === 'string' ? value.slice(0, 10) : (value ?? '');

  return (
    <input
      className={base}
      style={style}
      type={inputType}
      disabled={disabled}
      value={shown}
      onChange={(e) => {
        const raw = e.target.value;
        if (field.type === 'number' || field.type === 'currency') onChange(raw === '' ? '' : Number(raw));
        else onChange(raw);
      }}
    />
  );
}

/** A table answer: rows of column → value, add and remove. */
function TableInput({ field, value, onChange, disabled }) {
  const rows = Array.isArray(value) ? value : [];
  const columns = field.columns || [];

  const setCell = (r, col, v) => onChange(rows.map((row, i) => (i === r ? { ...row, [col]: v } : row)));
  const addRow = () => onChange([...rows, Object.fromEntries(columns.map((c) => [c, '']))]);
  const removeRow = (r) => onChange(rows.filter((_, i) => i !== r));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left px-2 py-1.5 font-semibold" style={{ color: MUTED, borderBottom: `1px solid ${BORDER}` }}>{c}</th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r}>
              {columns.map((c) => (
                <td key={c} className="px-1 py-1">
                  <input
                    className="w-full px-2 py-1.5 rounded-md text-[12px] outline-none"
                    style={{ background: '#fffdf8', border: `1px solid ${BORDER}` }}
                    disabled={disabled}
                    value={row[c] ?? ''}
                    onChange={(e) => setCell(r, c, e.target.value)}
                  />
                </td>
              ))}
              <td className="px-1">
                {!disabled && (
                  <button type="button" onClick={() => removeRow(r)} aria-label="Remove row" className="p-1" style={{ color: MUTED }}>
                    <FiX size={14} />
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="px-2 py-2 text-[12px]" style={{ color: MUTED }}>
                {field.required ? 'Nothing here yet.' : 'None. Leaving this empty records it as nil.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && (
        <button type="button" onClick={addRow} className="mt-1.5 flex items-center gap-1 text-[12px] font-semibold" style={{ color: TEAL }}>
          <FiPlus size={13} /> Add a row
        </button>
      )}
    </div>
  );
}

export default function McaFormPreparePage() {
  const { activeCompany } = useApp();
  const { showToast } = useUI();

  const companyId = activeCompany?._id || activeCompany?.id || null;
  const years = useMemo(() => financialYearOptions(), []);

  const [financialYear, setFinancialYear] = useState(years[1] || years[0]); // returns are usually for last year
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [payload, setPayload] = useState(null); // { draft, form }
  const [edits, setEdits] = useState({});        // unsaved changes, by field key
  const [srn, setSrn] = useState('');

  const formKey = 'mgt7a';
  const draft = payload?.draft || null;
  const form = payload?.form || null;
  const filed = draft?.status === 'filed';
  const dirty = Object.keys(edits).length > 0;

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Opening is idempotent: it returns the draft you already have.
      const res = await mcaFormsAPI.open(companyId, formKey, financialYear);
      setPayload(res?.data || null);
      setEdits({});
    } catch (error) {
      showToast(errText(error), 'error');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, financialYear, showToast]);

  useEffect(() => { setPayload(null); setEdits({}); }, [companyId, financialYear]);

  const valueOf = (key) => (key in edits ? edits[key] : draft?.values?.[key]);
  const setValue = (key, v) => setEdits((e) => ({ ...e, [key]: v }));

  const save = async () => {
    if (!dirty) return;
    setBusy('save');
    try {
      const res = await mcaFormsAPI.save(companyId, formKey, financialYear, edits);
      setPayload(res?.data || null);
      setEdits({});
      showToast('Saved.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const refresh = async () => {
    setBusy('refresh');
    try {
      const res = await mcaFormsAPI.refresh(companyId, formKey, financialYear);
      setPayload(res?.data || null);
      showToast(res?.message || 'Refreshed.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const download = async () => {
    setBusy('export');
    try {
      const res = await mcaFormsAPI.exportDraft(companyId, formKey, financialYear);
      const blob = new Blob([JSON.stringify(res?.data || {}, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${draft?.companyCin || 'company'}-${form?.code || 'form'}-${financialYear}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const recordFiled = async () => {
    setBusy('filed');
    try {
      const res = await mcaFormsAPI.markFiled(companyId, formKey, financialYear, srn);
      setPayload(res?.data || null);
      showToast(res?.message || 'Recorded.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const validation = draft?.validation || {};
  const missing = new Set(validation.missing || []);
  const problemsByField = useMemo(() => {
    const map = {};
    for (const p of validation.problems || []) (map[p.field] = map[p.field] || []).push(p.message);
    return map;
  }, [validation.problems]);

  const labelOf = (key) => {
    for (const section of form?.sections || []) {
      const hit = section.fields.find((f) => f.key === key);
      if (hit) return hit.label;
    }
    return key;
  };

  return (
    <AppLayout pageTitle="Prepare MCA Return">
      <PageHeader
        title="Prepare an MCA return"
        subtitle={activeCompany ? `${activeCompany.name} · MGT-7A, the abridged annual return` : 'Select a company to begin'}
      />

      {/* The thing people assume otherwise about, said first. */}
      <div
        className="flex items-start gap-2 mb-5 px-4 py-3 rounded-lg text-[12.5px] leading-relaxed"
        style={{ background: '#edeef3', color: '#3a4a66', border: '1px solid #d8dce6' }}
      >
        <FiInfo size={15} className="shrink-0 mt-0.5" />
        <div>
          <strong>This prepares the return — it does not file it.</strong> MCA has no way for software to submit a
          form. When this is complete, a director signs it with their DSC and files it on the MCA portal; come back
          here and record the SRN.
        </div>
      </div>

      {!companyId && (
        <Card><div className="text-[13px]" style={{ color: MUTED }}>Choose a company in the sidebar to prepare its return.</div></Card>
      )}

      {companyId && (
        <Card className="mb-5">
          <div className="flex items-end gap-3 flex-wrap">
            <label className="block">
              <span className="block text-[11.5px] font-semibold mb-1" style={{ color: MUTED }}>Financial year</span>
              <select
                className="px-3 py-2 rounded-lg text-[13px]"
                style={{ background: '#fffdf8', border: `1px solid ${BORDER}` }}
                value={financialYear}
                onChange={(e) => setFinancialYear(e.target.value)}
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <Button onClick={load} loading={loading} disabled={!!draft && !loading}>
              {draft ? 'Draft open' : 'Start / reopen the draft'}
            </Button>
          </div>
        </Card>
      )}

      {draft && form && (
        <>
          {/* Where it stands */}
          <Card className="mb-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-[13px]">
                {filed ? (
                  <><FiCheckCircle style={{ color: TEAL }} /> <strong>Recorded as filed</strong> · SRN {draft.srn}</>
                ) : draft.status === 'ready' ? (
                  <><FiCheckCircle style={{ color: TEAL }} /> <strong>Ready to sign.</strong> Every required answer is in and nothing looks wrong.</>
                ) : (
                  <><FiAlertTriangle style={{ color: GOLD }} /> <strong>Not ready yet.</strong>
                    {missing.size > 0 && ` ${missing.size} required answer${missing.size === 1 ? '' : 's'} missing.`}
                    {(validation.problems || []).length > 0 && ` ${validation.problems.length} thing${validation.problems.length === 1 ? '' : 's'} to check.`}
                  </>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {!filed && (
                  <Button variant="outline" size="sm" leftIcon={<FiRefreshCw size={13} />} onClick={refresh} loading={busy === 'refresh'} disabled={dirty}>
                    Refresh from registers
                  </Button>
                )}
                <Button variant="outline" size="sm" leftIcon={<FiDownload size={13} />} onClick={download} loading={busy === 'export'}>
                  Download
                </Button>
                {!filed && (
                  <Button size="sm" onClick={save} loading={busy === 'save'} disabled={!dirty}>
                    {dirty ? 'Save changes' : 'Saved'}
                  </Button>
                )}
              </div>
            </div>

            {/* What is wrong, apart from what is missing. */}
            {(validation.problems || []).length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {validation.problems.map((p, i) => (
                  <li key={i} className="text-[12.5px] flex gap-1.5" style={{ color: '#9a564a' }}>
                    <FiAlertTriangle size={13} className="shrink-0 mt-0.5" />
                    <span><strong>{p.field === '_form' ? 'This form' : labelOf(p.field)}:</strong> {p.message}</span>
                  </li>
                ))}
              </ul>
            )}
            {missing.size > 0 && (
              <div className="mt-3 text-[12px]" style={{ color: MUTED }}>
                Still to answer: {[...missing].map(labelOf).join(' · ')}
              </div>
            )}
          </Card>

          {/* The form itself, section by section. */}
          {form.sections.map((section) => (
            <Card key={section.id} className="mb-4">
              <div className="text-[14px] font-bold mb-3" style={{ color: '#08111f' }}>{section.title}</div>
              <div className="space-y-4">
                {section.fields.map((field) => {
                  const value = valueOf(field.key);
                  const source = draft.provenance?.[field.key];
                  const isMissing = missing.has(field.key) && isBlank(value);
                  const flagged = problemsByField[field.key];
                  return (
                    <div key={field.key}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[12.5px] font-semibold" style={{ color: '#08111f' }}>
                          {field.label}{field.required && <span style={{ color: '#9a564a' }}> *</span>}
                        </span>
                        {source === 'prefill' && !(field.key in edits) && (
                          <span className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: '#e8efe5', color: '#4a6b3f' }}>from ComplyV</span>
                        )}
                        {(source === 'user' || field.key in edits) && (
                          <span className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: '#f3ebd7', color: GOLD }}>entered by you</span>
                        )}
                        {isMissing && (
                          <span className="text-[10.5px] px-2 py-0.5 rounded-full" style={{ background: '#f7e8e5', color: '#9a564a' }}>required</span>
                        )}
                      </div>
                      <FieldInput field={field} value={value} onChange={(v) => setValue(field.key, v)} disabled={filed} />
                      {field.help && <div className="text-[11.5px] mt-1" style={{ color: MUTED }}>{field.help}</div>}
                      {flagged && flagged.map((m) => (
                        <div key={m} className="text-[11.5px] mt-1" style={{ color: '#9a564a' }}>{m}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}

          {/* After the person has filed on the portal. */}
          {draft.status === 'ready' && !filed && (
            <Card className="mb-8">
              <div className="text-[14px] font-bold mb-1" style={{ color: '#08111f' }}>Filed it on the MCA portal?</div>
              <div className="text-[12.5px] mb-3" style={{ color: MUTED }}>
                Record the SRN from the MCA acknowledgement. This only notes what you did there — ComplyV has not
                filed anything.
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  className="px-3 py-2 rounded-lg text-[13px] outline-none w-[200px]"
                  style={{ background: '#fffdf8', border: `1px solid ${BORDER}`, fontFamily: 'monospace' }}
                  placeholder="T12345678"
                  maxLength={9}
                  value={srn}
                  onChange={(e) => setSrn(e.target.value.toUpperCase().trim())}
                />
                <Button onClick={recordFiled} loading={busy === 'filed'} disabled={!/^[A-Z]\d{8}$/.test(srn)}>
                  Record SRN
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </AppLayout>
  );
}
